import { useSupabase } from '@packages/supabase'
import { useCallback } from 'react'
import useSWR from 'swr'
import type {
  CreateCredentialResult,
  IntegrationApiLogRow,
  IntegrationCredentialRow
} from '../types/integrationScopes'

export function useIntegrationCredentials(enabled: boolean) {
  const { supabase, tenantId } = useSupabase()

  const credentials = useSWR(
    enabled && tenantId ? ['integration-credentials', tenantId] : null,
    async () => {
      const { data, error } = await supabase.rpc(
        'list_tenant_integration_credentials'
      )
      if (error) throw error
      return (data ?? []) as IntegrationCredentialRow[]
    }
  )

  const logs = useSWR(
    enabled && tenantId ? ['integration-api-logs', tenantId] : null,
    async () => {
      const { data, error } = await supabase
        .from('integration_api_logs')
        .select(
          'id, operation, status_code, success, duration_ms, origin, error_code, created_at, credential_id'
        )
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })
        .limit(30)

      if (error) throw error
      return (data ?? []) as IntegrationApiLogRow[]
    }
  )

  const createCredential = useCallback(
    async (input: {
      name: string
      credentialType: 'publishable' | 'secret'
      scopes: string[]
      allowedOrigins: string[]
      environment: 'live' | 'test'
    }) => {
      const { data, error } = await supabase.rpc(
        'create_tenant_integration_credential',
        {
          p_name: input.name.trim(),
          p_credential_type: input.credentialType,
          p_scopes: input.scopes,
          p_allowed_origins: input.allowedOrigins,
          p_environment: input.environment
        }
      )
      if (error) throw error
      await credentials.mutate()
      return data as CreateCredentialResult
    },
    [supabase, credentials]
  )

  const revokeCredential = useCallback(
    async (credentialId: string) => {
      const { error } = await supabase.rpc(
        'revoke_tenant_integration_credential',
        { p_credential_id: credentialId }
      )
      if (error) throw error
      await credentials.mutate()
    },
    [supabase, credentials]
  )

  return {
    credentials: credentials.data ?? [],
    credentialsLoading: credentials.isLoading,
    credentialsError: credentials.error,
    refreshCredentials: credentials.mutate,
    logs: logs.data ?? [],
    logsLoading: logs.isLoading,
    logsError: logs.error,
    createCredential,
    revokeCredential
  }
}
